import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { getApplications, getPrograms } from '@/lib/api';
import DataTable from '@/components/admin/DataTable';
import ApplicantActions from '@/components/admin/ApplicantActions';
import PortalEmailComposer from '@/components/admin/PortalEmailComposer';
import SourceCell from '@/components/admin/SourceCell';
import { heardAboutSortValue } from '@/lib/heardAbout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  SEMESTER_OPTIONS,
  ALL_STATUSES,
  statusMeta,
  badgeClass,
  formatDate,
} from '@/lib/applications';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  Files,
} from 'lucide-react';

const PAGE_SIZE = 25;
const ALL = 'all';
const ASAP_START_VALUE = 'asap';
const STARTING_MONTH_COUNT = 24;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function monthOptionValue(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
}

function monthOptionLabel(value) {
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function intendedStartingDateDisplay(value) {
  if (!value) return '—';
  if (value === ASAP_START_VALUE) return 'As soon as possible';
  if (ISO_DATE_RE.test(value)) return formatDate(value);
  return value;
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function programStartingDateValues(programs) {
  const values = new Set();
  safeArray(programs).forEach((program) => {
    safeArray(program?.intakes).forEach((item) => {
      const value = typeof item === 'string' ? item : item?.start;
      const text = String(value || '').trim();
      if (text) values.add(text);
    });
    safeArray(program?.semesters).forEach((item) => {
      const text = String(item || '').trim();
      if (text) values.add(text);
    });
  });
  return [...values].sort((a, b) => a.localeCompare(b));
}

function buildStartingDateFilterOptions(programs = []) {
  const programDates = programStartingDateValues(programs);
  if (programDates.length) {
    return [
      { value: ASAP_START_VALUE, label: 'As soon as possible' },
      ...programDates.map((value) => ({ value, label: intendedStartingDateDisplay(value) })),
    ];
  }
  const now = new Date();
  const startYear = now.getFullYear();
  const startMonth = now.getMonth();
  const monthOptions = Array.from({ length: STARTING_MONTH_COUNT }, (_, index) => {
    const date = new Date(Date.UTC(startYear, startMonth + index, 1));
    const value = monthOptionValue(date);
    return { value, label: monthOptionLabel(value) };
  });
  const legacyOptions = SEMESTER_OPTIONS.map((value) => ({ value, label: `${value} (legacy)` }));
  return [
    { value: ASAP_START_VALUE, label: 'As soon as possible' },
    ...monthOptions,
    ...legacyOptions,
  ];
}

function programNamesFromRecords(records) {
  return [...new Set((records || [])
    .map((program) => String(program?.name || '').trim())
    .filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));
}

function applicantInitials(application) {
  return [application?.first_name, application?.last_name]
    .filter(Boolean)
    .map((part) => String(part).trim()[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'AP';
}

export default function AdminApplications() {
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();

  const [items, setItems] = useState([]);
  const [programRecords, setProgramRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [programFilter, setProgramFilter] = useState(ALL);
  const [startingDateFilter, setStartingDateFilter] = useState(ALL);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const id = window.setTimeout(() => setQ(searchInput.trim()), 350);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, programFilter, startingDateFilter, q]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, statusFilter, programFilter, startingDateFilter, q]);

  useEffect(() => {
    if (authLoading) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
        if (statusFilter !== ALL) params.status = statusFilter;
        if (programFilter !== ALL) params.program = programFilter;
        if (startingDateFilter !== ALL) params.intended_semester = startingDateFilter;
        if (q) params.q = q;
        const response = await getApplications(params);
        if (cancelled) return;
        setItems(response.data?.items || []);
        setTotal(response.data?.total || 0);
      } catch (error) {
        if (cancelled) return;
        console.error(error);
        toast.error(error?.response?.data?.detail || 'Could not load applications');
        setItems([]);
        setTotal(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, statusFilter, programFilter, startingDateFilter, q, page, refreshKey]);

  useEffect(() => {
    if (authLoading) return undefined;
    let cancelled = false;
    getPrograms()
      .then((response) => {
        if (!cancelled) setProgramRecords(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error(error);
          setProgramRecords([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [authLoading]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, page * PAGE_SIZE + items.length);
  const startingDateOptions = useMemo(
    () => buildStartingDateFilterOptions(programRecords),
    [programRecords],
  );
  const programOptions = useMemo(() => {
    const names = new Set(programNamesFromRecords(programRecords));
    items.forEach((item) => {
      const name = String(item?.program || '').trim();
      if (name) names.add(name);
    });
    if (programFilter !== ALL) names.add(programFilter);
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [items, programFilter, programRecords]);

  const fullName = (application) => (
    `${application.first_name || ''} ${application.last_name || ''}`.trim() || 'Unnamed applicant'
  );
  const submittedDate = (application) => (
    Object.prototype.hasOwnProperty.call(application || {}, 'submitted_at')
      ? application.submitted_at
      : application.created_at
  );

  const selectedApplications = items.filter((item) => selectedIds.has(item.id));
  const summary = useMemo(() => ({
    total,
    visible: items.length,
    needsReview: items.filter((item) => ['applied', 'under_review'].includes(item.status)).length,
    progressed: items.filter((item) => ['offered', 'enrolled'].includes(item.status)).length,
  }), [items, total]);

  const columns = [
    {
      id: 'student_number',
      header: 'Applicant ID',
      sortable: true,
      sortAccessor: (application) => application.application_number || application.converted_student_number || '',
      cellClassName: 'min-w-[130px] text-sm font-medium text-[#062B1A]',
      cell: (application) => application.application_number || application.converted_student_number || 'Not assigned',
    },
    {
      id: 'applicant',
      header: 'Applicant Name',
      sortable: true,
      sortAccessor: fullName,
      cellClassName: 'min-w-[220px]',
      cell: (application) => (
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#062B1A] text-xs font-semibold text-white">
            {applicantInitials(application)}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#062B1A]">{fullName(application)}</p>
            <p className="truncate text-xs text-[#062B1A]/55">{application.email || 'Email not provided'}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'program',
      header: 'Program',
      sortable: true,
      sortAccessor: (application) => application.program || '',
      cellClassName: 'min-w-[190px] text-sm text-[#062B1A]/70',
      cell: (application) => application.program || 'Program not selected',
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      sortAccessor: (application) => application.status || '',
      cellClassName: 'min-w-[140px]',
      cell: (application) => {
        const meta = statusMeta(application.status);
        return (
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="outline" className={badgeClass(meta.tone)}>{meta.label}</Badge>
            {application.student_profile_deleted && (
              <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">
                Student profile deleted
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      id: 'source',
      header: 'Source',
      sortable: true,
      sortAccessor: heardAboutSortValue,
      cellClassName: 'min-w-[170px]',
      cell: (application) => (
        <SourceCell
          record={application}
          onOpenAgency={(agentId) => navigate(`/admin/agencies/${agentId}`)}
        />
      ),
    },
    {
      id: 'starting_date',
      header: 'Start Date',
      sortable: true,
      sortAccessor: (application) => application.intended_semester || '',
      cellClassName: 'min-w-[150px] text-sm text-[#062B1A]/70',
      cell: (application) => intendedStartingDateDisplay(application.intended_semester),
    },
    {
      id: 'submitted',
      header: 'Submitted Date',
      sortable: true,
      sortAccessor: submittedDate,
      cellClassName: 'min-w-[120px] text-sm text-[#062B1A]/60',
      cell: (application) => formatDate(submittedDate(application)),
    },
  ];

  const filters = [
    {
      id: 'program',
      value: programFilter,
      onChange: setProgramFilter,
      options: [
        { value: ALL, label: 'All programs' },
        ...programOptions.map((program) => ({ value: program, label: program })),
      ],
    },
    {
      id: 'status',
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: ALL, label: 'All statuses' },
        ...ALL_STATUSES.map((status) => ({ value: status, label: statusMeta(status).label })),
      ],
    },
    {
      id: 'starting-date',
      value: startingDateFilter,
      onChange: setStartingDateFilter,
      options: [
        { value: ALL, label: 'All start dates' },
        ...startingDateOptions,
      ],
    },
  ];

  const summaryCards = [
    { label: 'Applications', value: summary.total, icon: Files },
    { label: 'Visible', value: summary.visible, icon: CalendarClock, accent: true },
    { label: 'Needs Review', value: summary.needsReview, icon: AlertTriangle },
    { label: 'Offered / Enrolled', value: summary.progressed, icon: CheckCircle2 },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-2">
        <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          Admissions
        </h2>
        <p className="text-sm text-[#062B1A]/60">
          Search, sort, review, and follow up with applicants from one management table.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map(({ label, value, icon: Icon, accent }) => (
          <Card
            key={label}
            className={`border border-[rgba(6,43,26,0.08)] shadow-sm${accent ? ' gold-border-left' : ''}`}
          >
            <CardContent className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">{label}</span>
                <Icon className="h-4 w-4 text-[#D4AF37]" />
              </div>
              <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                {value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <DataTable
        title="Admissions Management"
        actionsBelowSearch
        data={items}
        columns={columns}
        loading={loading}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Search anything..."
        filters={filters}
        selectedIds={selectedIds}
        onSelectedIdsChange={setSelectedIds}
        bulkActions={({ selectedCount }) => (
          <>
            <ApplicantActions
              selectedApplications={selectedApplications}
              programs={programOptions}
              onChanged={() => {
                setSelectedIds(new Set());
                setPage(0);
                setRefreshKey((key) => key + 1);
              }}
            />
            <PortalEmailComposer
              audience="applications"
              recipients={selectedApplications}
              buttonLabel="Email selected"
              disabled={selectedCount === 0}
              buttonClassName="border-[#D4AF37]/45 text-[#7A5A05]"
            />
          </>
        )}
        rowActions={(application) => (
          <div className="flex justify-end gap-1.5">
            <PortalEmailComposer
              audience="applications"
              recipients={[application]}
              buttonLabel={`Email ${fullName(application)}`}
              buttonSize="icon"
              buttonClassName="border-[#062B1A]/15 text-[#062B1A]"
              iconOnly
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              title="View application"
              aria-label={`View ${fullName(application)}`}
              className="border-[#062B1A]/15 text-[#062B1A]"
              onClick={() => navigate(`/admin/applications/${application.id}`)}
            >
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        )}
        onRowClick={(application) => navigate(`/admin/applications/${application.id}`)}
        emptyMessage="No applications match the current filters."
        pageSize={PAGE_SIZE}
        showPagination={false}
      />

      <div className="flex items-center justify-between">
        <p className="text-xs text-[#062B1A]/60">
          Showing {rangeStart} - {rangeEnd} of {total} · Page {page + 1} of {pageCount}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0 || loading}
            onClick={() => setPage((current) => Math.max(0, current - 1))}
            className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
          >
            <ChevronLeft className="mr-1 h-4 w-4" /> Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page + 1 >= pageCount || loading}
            onClick={() => setPage((current) => current + 1)}
            className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
          >
            Next <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
